import React from 'react';
import { TrendingUp, AlertTriangle } from 'lucide-react';

/**
 * CoverageBar Component
 * Displays schedule coverage percentage with a visual progress bar
 *
 * @param {Object} props
 * @param {number} props.percentage - Coverage percentage (0-100)
 * @param {number} props.covered - Number of days with scheduled content
 * @param {number} props.total - Total number of days in range
 * @param {number} [props.gaps] - Number of gap days
 */
const CoverageBar = ({
    percentage,
    covered,
    total,
    gaps = 0
}) => {
    // Determine color based on coverage
    const getColor = () => {
        if (percentage >= 80) return 'var(--color-success)';
        if (percentage >= 50) return 'var(--color-warning)';
        return 'var(--color-error)';
    };

    const barColor = getColor();

    return (
        <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-4)',
            padding: 'var(--space-3) var(--space-4)',
            background: 'var(--color-bg-secondary)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--color-border-light)'
        }}>
            {/* Icon */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '32px',
                height: '32px',
                borderRadius: 'var(--radius-md)',
                background: barColor,
                color: 'white'
            }}>
                {percentage < 50 ? <AlertTriangle size={18} /> : <TrendingUp size={18} />}
            </div>

            {/* Progress section */}
            <div style={{ flex: 1 }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 'var(--space-1)'
                }}>
                    <span style={{
                        fontSize: 'var(--font-size-sm)',
                        fontWeight: 600
                    }}>
                        Coverage: {percentage}%
                    </span>
                    <span style={{
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--color-text-secondary)'
                    }}>
                        {covered}/{total} days
                    </span>
                </div>

                {/* Progress bar */}
                <div style={{
                    width: '100%',
                    height: '6px',
                    background: 'var(--color-bg-tertiary)',
                    borderRadius: 'var(--radius-full)',
                    overflow: 'hidden'
                }}>
                    <div style={{
                        width: `${percentage}%`,
                        height: '100%',
                        background: barColor,
                        borderRadius: 'var(--radius-full)',
                        transition: 'width 0.3s ease'
                    }} />
                </div>
            </div>

            {/* Gaps indicator */}
            {gaps > 0 && (
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    padding: '0 var(--space-3)',
                    borderLeft: '1px solid var(--color-border-light)'
                }}>
                    <span style={{
                        fontSize: 'var(--font-size-lg)',
                        fontWeight: 700,
                        color: 'var(--color-error)'
                    }}>
                        {gaps}
                    </span>
                    <span style={{
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--color-text-tertiary)'
                    }}>
                        gaps
                    </span>
                </div>
            )}
        </div>
    );
};

export default CoverageBar;
